import React, { useCallback, useEffect, useState } from 'react';
import { LicenseEditor } from '../../vendor/prism-components';

/**
 * <SaasLicensePanel> - Einstieg in die bestehenden SaaS-Tool-Lizenzen eines Mandanten.
 *
 * Nutzt die PRISM-OS-eigenen Endpunkte (useApi -> /api/platform/admin), weil die
 * Mandantenliste dieses Managers aus den PlatformTenants kommt (gleiche id-Raum):
 *   GET /admin/tenants/{id}/licenses   -> Array ToolLicense
 *   PUT /admin/licenses/{license_id}   -> aktualisieren
 *   POST /admin/tenants/{id}/licenses  -> neue SaaS-Lizenz
 * Der bestehende <LicenseEditor> wird je ToolLicense wiederverwendet (nicht neu gebaut).
 *
 * Props: tenantId, api (useApi().api), canManage, showToast, t.
 */
export default function SaasLicensePanel({ tenantId, api, canManage, showToast, t }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});     // id -> {tier, features, valid_until}
  const [saving, setSaving] = useState({});     // id -> bool
  const [msg, setMsg] = useState({});           // id -> string
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/admin/tenants/${tenantId}/licenses`);
      const list = Array.isArray(data) ? data : [];
      setLicenses(list);
      const d = {};
      list.forEach((l) => {
        d[l.id] = { tier: l.tier, features: l.features || {}, valid_until: l.valid_until || '' };
      });
      setDrafts(d);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, showToast, t]);

  useEffect(() => { load(); }, [load]);

  const save = async (lic) => {
    const draft = drafts[lic.id];
    if (!draft) return;
    setSaving((s) => ({ ...s, [lic.id]: true }));
    setMsg((m) => ({ ...m, [lic.id]: '' }));
    try {
      await api(`/admin/licenses/${lic.id}`, {
        method: 'PUT',
        body: { tier: draft.tier, features: draft.features, valid_until: draft.valid_until || null },
      });
      setMsg((m) => ({ ...m, [lic.id]: t('shared.license.saved', 'Lizenzen gespeichert') }));
      await load();
    } catch (err) {
      setMsg((m) => ({ ...m, [lic.id]: t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || '') }));
    } finally {
      setSaving((s) => ({ ...s, [lic.id]: false }));
    }
  };

  const addLicense = async (toolId) => {
    setAdding(true);
    try {
      await api(`/admin/tenants/${tenantId}/licenses`, {
        method: 'POST',
        body: { tool_id: toolId, tier: 'professional', valid_until: null, environments: ['prod'], features: {} },
      });
      showToast(t('license.saas.added', 'SaaS-Lizenz angelegt'), 'success');
      await load();
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.loading', 'Lade...')}</div>;
  }

  const existingTools = new Set(licenses.map((l) => l.tool_id));
  const addable = ['p4i', 'p4a'].filter((tid) => !existingTools.has(tid));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {licenses.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          {t('license.saas.empty', 'Keine SaaS-Tool-Lizenzen fuer diesen Mandanten.')}
        </div>
      )}

      {licenses.map((lic) => (
        <div key={lic.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: 'var(--bg-surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{lic.tool_id}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {(lic.environments || ['prod']).join(', ')}
              {lic.is_active === false ? ` - ${t('common.inactive', 'Inaktiv')}` : ''}
            </span>
          </div>
          <LicenseEditor
            licenses={drafts[lic.id]}
            onChange={(next) => setDrafts((d) => ({ ...d, [lic.id]: next }))}
            onSave={canManage ? () => save(lic) : undefined}
            saving={!!saving[lic.id]}
            saveMessage={msg[lic.id] || ''}
            disabled={!canManage}
            t={t}
          />
        </div>
      ))}

      {canManage && addable.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('license.saas.add', 'SaaS-Lizenz hinzufuegen')}:</span>
          {addable.map((tid) => (
            <button key={tid} type="button" className="btn btn-ghost btn-sm" onClick={() => addLicense(tid)} disabled={adding}>
              + {tid}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
